import moment from "moment";

export const DAY_LABELS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

export const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

export const titleCase = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Raw market rows -> [{name, slug, openTime, closeTime, status, isOpen}]
export function normalizeMarkets(raw, now = moment()) {
  const today = now.format("dddd").toLowerCase();
  return (Array.isArray(raw) ? raw : [])
    .filter((m) => m && m.gameName && m.isActive !== false)
    .map((m) => {
      const day = (m.weekends || []).find((w) => String(w.day).toLowerCase() === today);
      let isOpen = !!m.is_open;
      let openTime = m.openTime;
      let closeTime = m.closeTime;
      if (day) {
        openTime = day.openTime || openTime;
        closeTime = day.closeTime || closeTime;
        isOpen = day.is_on === false ? false : !!day.is_open;
      }
      return {
        name: String(m.gameName).trim().toUpperCase(),
        slug: slugify(m.gameName),
        openTime,
        closeTime,
        isOpen,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

const digitSum = (panna) =>
  String(panna)
    .split("")
    .reduce((acc, n) => acc + (parseInt(n, 10) || 0), 0) % 10;

// Declared rows -> { slug: { 'YYYY-MM-DD': {open, close, jodi} } }
export function groupResults(rawResults) {
  const bySlug = {};
  (rawResults || []).forEach((r) => {
    if (!r || !r.gameName || !r.date) return;
    const slug = slugify(r.gameName);
    const days = (bySlug[slug] = bySlug[slug] || {});
    const d = (days[r.date] = days[r.date] || { open: "", close: "", jodi: "" });
    const type = String(r.gameType || "").toLowerCase();
    if (type === "open") d.open = String(r.panna);
    if (type === "close") d.close = String(r.panna);
  });
  Object.values(bySlug).forEach((days) =>
    Object.values(days).forEach((d) => {
      if (d.open && d.close) d.jodi = `${digitSum(d.open)}${digitSum(d.close)}`;
      else if (d.open) d.jodi = `${digitSum(d.open)}*`;
      else if (d.close) d.jodi = `*${digitSum(d.close)}`;
      else d.jodi = "**";
    })
  );
  return bySlug;
}

// One market's days -> weekly rows, newest week first. Each cell is a day object or null.
export function buildWeeks(days) {
  const keys = Object.keys(days || {}).filter((k) => moment(k, "YYYY-MM-DD", true).isValid());
  if (!keys.length) return [];
  keys.sort();
  const first = moment(keys[0], "YYYY-MM-DD").startOf("isoWeek");
  const last = moment(keys[keys.length - 1], "YYYY-MM-DD").startOf("isoWeek");
  const weeks = [];
  for (let start = first.clone(); !start.isAfter(last); start.add(1, "week")) {
    const cells = [];
    for (let i = 0; i < 7; i += 1) {
      cells.push(days[start.clone().add(i, "day").format("YYYY-MM-DD")] || null);
    }
    weeks.push({
      key: start.format("YYYY-MM-DD"),
      from: start.format("DD/MM/YYYY"),
      to: start.clone().add(6, "day").format("DD/MM/YYYY"),
      cells,
    });
  }
  return weeks.reverse();
}

// Latest declared (any half) day of a market.
export function latestDay(days) {
  const keys = Object.keys(days || {}).sort();
  for (let i = keys.length - 1; i >= 0; i -= 1) {
    const d = days[keys[i]];
    if (d.open || d.close) return { date: keys[i], ...d };
  }
  return null;
}

export const resultString = (d) => {
  if (!d) return "XXX - XX - XXX";
  const open = d.open || "XXX";
  const close = d.close || "XXX";
  const jodi = d.jodi && d.jodi !== "**" ? d.jodi : "XX";
  return `${open} - ${jodi.replace("*", "X")} - ${close}`;
};

export const isDouble = (jodi) => /^(\d)\1$/.test(jodi || "");

// ---------- Starline / Jackpot (hourly single-result slots) ----------

// Game-list rows (or fixed slots) -> [{name, slug}]
export function normalizeSlots(rawList, fallback, nameKeys = ["game_name", "gameName", "name"]) {
  const names = (Array.isArray(rawList) ? rawList : [])
    .map((r) => {
      for (const k of nameKeys) if (r && r[k]) return String(r[k]).trim();
      return "";
    })
    .filter(Boolean);
  const list = names.length ? names : fallback;
  return list.map((name) => ({ name: name.toUpperCase(), slug: slugify(name) }));
}

// Starline rows {market, date, panna, digit} -> { slug: { date: {panna, digit} } }
export function groupStarline(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    if (!r || !r.market || !r.date) return;
    const days = (out[slugify(r.market)] = out[slugify(r.market)] || {});
    days[r.date] = { panna: String(r.panna ?? ""), digit: String(r.digit ?? "") };
  });
  return out;
}

// Jackpot rows {gameName, date, leftDigit, jodiDigit, rightDigit} -> { slug: { date: {value} } }
export function groupJackpot(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    if (!r || !r.gameName || !r.date) return;
    const jodi = String(r.jodiDigit ?? "");
    const value = jodi.length >= 2 ? jodi : `${r.leftDigit ?? ""}${jodi}${r.rightDigit ?? ""}`;
    const days = (out[slugify(r.gameName)] = out[slugify(r.gameName)] || {});
    days[r.date] = { value };
  });
  return out;
}

// Open / closed badge for an hourly slot such as "10:30 AM".
export function slotStatus(name, now = moment()) {
  const t = moment(name, "hh:mm A", true);
  if (!t.isValid()) return { open: true, text: "Open" };
  return now.isBefore(t) ? { open: true, text: "Open" } : { open: false, text: "Closed" };
}
